import { useState, useCallback, useEffect, useMemo, useRef, type CSSProperties } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  Sparkles,
  GitBranch,
  FileText,
  Loader2,
  ChevronDown,
  ChevronRight,
  Hammer,
  X,
  Compass,
  Check,
  Plus,
  HelpCircle,
  Database,
} from 'lucide-react'
import { createBlankProject } from '@/api/workbench'
import { prepareDraft, adoptDraftSources, getScopeBlock } from '@/api/generateapp'
import GuidedQuestions from '@/components/builder/GuidedQuestions'
import IdeasBrowser, { BusinessChip, type IdeaUseMode } from '@/components/builder/IdeasBrowser'
import IndustryPicker from '@/components/builder/IndustryPicker'
import FillInBlanks from '@/components/builder/FillInBlanks'
import type { DiscoverySolution, ScopeDecisions } from '@/types'
import AddSourceModal, {
  SOURCE_TYPES, type AttachedSource, type SourceKind,
} from '@/components/sources/AddSourceModal'
import AddSourceButton from '@/components/sources/AddSourceButton'
import StartBlankForm from '@/components/common/StartBlankForm'
import { useProjectStore } from '@/store/projectStore'
import { useDockUiStore } from '@/store/dockUiStore'
import { useAuth } from '@/auth/AuthContext'
import { setPendingBuild } from '@/lib/builderHandoff'
import { useAttachments } from '@/hooks/useAttachments'
import { useDiscoveryCatalog } from '@/hooks/useDiscoveryCatalog'
import AttachmentChips from '@/components/common/AttachmentChips'
import { ATTACHMENT_ACCEPT, toWire } from '@/lib/attachments'
import {
  buildIdeas, bySpecificity, fitsBusiness, indexCatalog, loadBusiness, pickFromIdea, popularIdeas,
  saveBusiness, unsurePick, type BusinessChoice, type HelpMeDecidePick, type Idea,
} from '@/lib/discovery'
import StartCard from '@/components/common/StartCard'

const slugify = (s: string): string =>
  s.trim().replace(/\s+/g, '_').replace(/[^A-Za-z0-9_-]/g, '').slice(0, 64)

// Words that say "build me an app" rather than what the app is about.
const NAME_FILLER = new Set([
  'a', 'an', 'the', 'i', 'we', 'me', 'my', 'our', 'want', 'need', 'would', 'like',
  'build', 'create', 'make', 'develop', 'app', 'application', 'system', 'tool',
  'to', 'for', 'that', 'which', 'with', 'and', 'of', 'please', 'can', 'you',
  'simple', 'small', 'new', 'web', 'online',
])

/** A working name from the description, for a build started without one.
 *  Every build now gets its own real project (no shared "Draft Application"
 *  slot); the user can rename it afterwards. */
const nameFromDescription = (desc: string): string => {
  const words = desc.split(/[^A-Za-z0-9]+/)
    .filter((w) => w && !NAME_FILLER.has(w.toLowerCase()))
    .slice(0, 3)
    .map((w) => w[0].toUpperCase() + w.slice(1))
  return words.join(' ') || 'My App'
}

/** ``base``, or ``base_2``, ``base_3``… — the first not already taken. */
const uniqueProjectId = (base: string, taken: Set<string>): string => {
  if (!taken.has(base.toLowerCase())) return base
  for (let n = 2; n < 100; n += 1) {
    const candidate = `${base.slice(0, 60)}_${n}`
    if (!taken.has(candidate.toLowerCase())) return candidate
  }
  return `${base.slice(0, 50)}_${Date.now().toString(36)}`
}

// Source kinds that PERSIST something into a project (a connection record, a
// registered reference source) — these need the draft to exist before the
// modal opens, and must follow the build into a newly named project.
const PERSISTING_KINDS: SourceKind[] = ['database', 'git', 'zip']

// The short-prompt checklist shows under a prompt this long (characters) —
// long enough to be a start, short enough that it probably leaves things out.
const NUDGE_MIN = 10
const NUDGE_MAX = 200

/** The hub's views: the start page, the ideas browser, fill in the blanks,
 *  and the guided questions → plan. One page, so nothing typed is lost. */
type View = 'start' | 'ideas' | 'blanks' | 'guided'

const VIEW_WIDTH: Record<View, number> = { start: 800, ideas: 1120, blanks: 840, guided: 1040 }

/** The answer typed after a checklist question that was added to the prompt. */
const answeredAfter = (text: string, question: string): boolean => text.split('\n').some((line) => {
  const at = line.indexOf(question)
  return at >= 0 && line.slice(at + question.length).trim().length >= 2
})

export default function BuilderHubPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { projects, fetchProjects, selectProject } = useProjectStore()
  const setProjectModalOpen = useDockUiStore((s) => s.setProjectModalOpen)
  const { catalog } = useDiscoveryCatalog()
  const idx = useMemo(() => (catalog ? indexCatalog(catalog) : null), [catalog])
  const ideas = useMemo(() => (catalog ? buildIdeas(catalog) : []), [catalog])

  const [view, setView] = useState<View>('start')
  // The app name is asked on the plan; a build started without one is named
  // from the description (renameable later).
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [creating, setCreating] = useState(false)
  // "Show me the plan before anything is built" — ON by default. Carried with
  // the pending build so the dock stops for the plan review (or not).
  const [reviewFirst, setReviewFirst] = useState(true)
  const containerRef = useRef<HTMLDivElement>(null)
  const descRef = useRef<HTMLTextAreaElement>(null)

  // Sources attached from the "+ Add a source" menu — same system as the
  // Ask-AI composer and the FRS builder. Each source's promptLine LEADS the
  // description so the build binds to the exact registered connection/source
  // instead of inferring one from the prose.
  const [sources, setSources] = useState<AttachedSource[]>([])
  // File attachments (PDF/Word specs, screenshots, data files). They ride the
  // build hand-off into the workspace dock, so a spec attached HERE reaches
  // the first FRS pass — that's why 'spec' is offered in the menu below.
  const att = useAttachments()
  const [sourceModalKind, setSourceModalKind] = useState<SourceKind | null>(null)
  const [preparingSource, setPreparingSource] = useState(false)

  // The user's business — optional, remembered per browser, and the one fact
  // that tunes the popular ideas, the browser and the questions' guidance.
  const [business, setBusiness] = useState<BusinessChoice | null>(() => loadBusiness(user?.user_id))
  useEffect(() => { setBusiness(loadBusiness(user?.user_id)) }, [user?.user_id])
  const [pickerOpen, setPickerOpen] = useState(false)
  // "Not sure at all?" asks the business first, then starts the questions.
  const [pickerThen, setPickerThen] = useState<'guided' | null>(null)

  // Where the description came from (an idea, or "not sure"): carries the
  // catalogue ids so the questions fit that kind of app.
  const [startedFrom, setStartedFrom] = useState<HelpMeDecidePick | null>(null)
  const [ideasCategory, setIdeasCategory] = useState<string | null>(null)
  // "Anything else?" written on fill in the blanks, kept on the plan.
  const [guidedNotes, setGuidedNotes] = useState('')
  // Remounts the questions for every new start (they fetch once per mount).
  const [guidedKey, setGuidedKey] = useState(0)

  useEffect(() => {
    containerRef.current?.scrollIntoView({ block: 'start' })
  }, [view])

  const withSources = useCallback((text: string) => (
    sources.length
      ? `${sources.map((s) => s.promptLine).join('\n')}\n${text}`.trim()
      : text
  ), [sources])

  // Persisting sources are staged into "Draft Application" (the project the
  // build may not have a name for yet) — prepare it so the connection /
  // reference-source APIs can resolve it. adopt-draft-sources moves them into
  // the named project at Build time.
  const openSourceModal = useCallback(async (kind: SourceKind) => {
    if (PERSISTING_KINDS.includes(kind)) {
      setPreparingSource(true)
      try {
        await prepareDraft()
      } catch {
        toast.error('Could not prepare the draft project for the source')
        setPreparingSource(false)
        return
      }
      setPreparingSource(false)
    }
    setSourceModalKind(kind)
  }, [])

  const handleCreateNew = useCallback(async (opts: { scopeBlock?: string; description?: string } = {}) => {
    const trimmedTitle = title.trim()
    const trimmedDesc = (opts.description ?? description).trim()
    if (trimmedDesc.length < 10) {
      toast.error('Tell me a bit more — at least 10 characters')
      return
    }
    // Every build gets its own real project: the name typed, or a working
    // name taken from the description (renameable later). Nothing is built
    // into a shared "Draft Application" slot any more.
    const appTitle = trimmedTitle || nameFromDescription(trimmedDesc)
    let projectId = slugify(appTitle)
    if (!projectId) {
      toast.error('App name must contain letters or numbers')
      return
    }
    if (!trimmedTitle) {
      const taken = new Set(projects.map((p) => (p.application?.folder_name || p.application?.id || '').toLowerCase()))
      projectId = uniqueProjectId(projectId, taken)
    }
    setCreating(true)
    try {
      const result = await createBlankProject(projectId, appTitle)
      projectId = result.project_id
      // Sources were staged into "Draft Application" (the app had no name
      // when they were attached) — move them into the project the build
      // will actually run in, or their prompt lines point at nothing.
      if (sources.some((s) => PERSISTING_KINDS.includes(s.kind))) {
        try {
          await adoptDraftSources(projectId)
        } catch (err) {
          toast.error(
            'Attached sources could not be moved into the new app — '
            + 'the agent may ask you to re-add them',
          )
          console.error('adopt-draft-sources failed', err)
        }
      }
      await fetchProjects()
      // Stash the build intent BEFORE selecting the project, so it's already
      // present when the dock's ModeRouter (re)mounts for the new project and
      // decides to run the orchestrator-backed dock.
      // A guided start already confirmed the scope — the block rides the brief
      // and the build skips its own scope round.
      const brief = opts.scopeBlock ? `${trimmedDesc}\n${opts.scopeBlock}` : trimmedDesc
      setPendingBuild(projectId, withSources(brief), att.attachments.map(toWire), reviewFirst)
      await selectProject(projectId)
      // Hand off to Builder Mode's workspace (live preview + persistent Ask-AI
      // dock). The dock runs the first-generation pipeline (FRS →
      // application.json → phase plan → review pause) via the orchestrator,
      // then the user drives the phases — the SAME dock UI used for ongoing
      // changes, so the agent looks identical everywhere.
      navigate(`/builder/${encodeURIComponent(projectId)}`)
    } catch (err: unknown) {
      const res = (err as { response?: { status?: number; data?: { detail?: unknown; code?: string } } })?.response
      // A plan refusal (e.g. the app limit) is already explained by the
      // AI-access window the API client opens — don't toast it twice.
      if (!(res?.status === 402 && res.data?.code)) {
        const detail = typeof res?.data?.detail === 'string' ? res.data.detail : ''
        toast.error(detail || 'Could not create the app — try a different name')
      }
      setCreating(false)
    }
  }, [title, description, sources, att, withSources, fetchProjects, selectProject, navigate, projects, reviewFirst])

  // Build from the guided plan: render the confirmed answers into the scope
  // block (server-side, one wording source), then the normal build hand-off.
  const buildGuided = useCallback(async (decisions: ScopeDecisions | null) => {
    let block = ''
    if (decisions) {
      setCreating(true)
      try {
        block = await getScopeBlock(decisions)
      } catch (err) {
        console.error('scope-block failed', err)
        toast.error('Could not save your answers — building from the description instead')
      }
      setCreating(false)
    }
    await handleCreateNew({ scopeBlock: block })
  }, [handleCreateNew])

  // The hub's picker for a solution type's "helps most" input (a file, a
  // database, a code source, a connector), when it has one.
  const helpsMostFor = useCallback((solution: DiscoverySolution | null): (() => void) | null => {
    const action = solution?.helps_most_action
    if (action === 'file') return att.openFilePicker
    if (action === 'database' || action === 'git' || action === 'ioflow') {
      return () => { void openSourceModal(action) }
    }
    return null
  }, [att.openFilePicker, openSourceModal])

  const focusPrompt = useCallback(() => {
    setTimeout(() => {
      const el = descRef.current
      if (!el) return
      el.focus({ preventScroll: true })
      el.setSelectionRange(el.value.length, el.value.length)
    }, 0)
  }, [])

  const startGuided = useCallback((pick: HelpMeDecidePick, text: string, notes = '') => {
    setStartedFrom(pick)
    setDescription(text)
    setGuidedNotes(notes)
    setGuidedKey((k) => k + 1)
    setView('guided')
  }, [])

  // Leaving the questions keeps every word: "Anything else?" notes (from fill
  // in the blanks or typed on the plan) join the description, so going back
  // and starting again never drops them.
  const returnFromGuided = useCallback((notes: string) => {
    const extra = notes.trim()
    if (extra) setDescription((d) => (d.includes(extra) ? d : `${d.trim()}\n\n${extra}`))
    setGuidedNotes('')
    setView('start')
    focusPrompt()
  }, [focusPrompt])

  const beginUnsure = useCallback((biz: BusinessChoice | null) => {
    const pick = unsurePick(biz, description)
    startGuided(pick, pick.text)
  }, [description, startGuided])

  const notSure = () => {
    if (!business && catalog) {
      setPickerThen('guided')
      setPickerOpen(true)
      return
    }
    beginUnsure(business)
  }

  const chooseBusiness = (choice: BusinessChoice | null) => {
    setBusiness(choice)
    saveBusiness(user?.user_id, choice)
    setPickerOpen(false)
    // An idea's starting text names the business — keep it in step while the
    // user hasn't edited it.
    if (startedFrom?.idea && idx) {
      const next = pickFromIdea(startedFrom.idea, choice, idx)
      if (description === startedFrom.text) setDescription(next.text)
      setStartedFrom(next)
    }
    if (pickerThen === 'guided') {
      setPickerThen(null)
      beginUnsure(choice)
    }
  }

  const startFromIdea = (idea: Idea, mode: IdeaUseMode) => {
    if (!idx) return
    const pick = pickFromIdea(idea, business, idx)
    if (mode === 'guided') { startGuided(pick, pick.text); return }
    setStartedFrom(pick)
    if (mode === 'blanks') { setView('blanks'); return }
    setDescription(pick.text)
    setView('start')
    focusPrompt()
  }

  const openIdeas = (category: string | null) => {
    setIdeasCategory(category)
    setView('ideas')
  }

  const blanksIdea = startedFrom?.idea || null
  const related = useMemo(() => {
    if (!blanksIdea) return []
    const bizId = business?.id || null
    return ideas
      .filter((i) => i.key !== blanksIdea.key && fitsBusiness(i, bizId)
        && (blanksIdea.area ? i.area === blanksIdea.area : i.solution === blanksIdea.solution))
      .sort(bySpecificity(bizId))
      .slice(0, 5)
  }, [ideas, blanksIdea, business])

  const popular = popularIdeas(ideas, business?.id || null)
  const doors = (catalog?.solution_groups || []).filter((g) => g.start_door)
  const trimmed = description.trim()
  const nudgeDims = catalog && !startedFrom && trimmed.length >= NUDGE_MIN && trimmed.length < NUDGE_MAX
    ? (catalog.nudge || []).map((id) => catalog.dimensions.find((d) => d.id === id)).filter((d): d is NonNullable<typeof d> => !!d)
    : []
  const addNudge = (question: string) => {
    if (!description.includes(question)) {
      setDescription((d) => `${d.replace(/\s+$/, '')}\n${question} `)
    }
    focusPrompt()
  }
  const startedHelps = helpsMostFor(startedFrom?.solution || null)

  return (
    <div
      ref={containerRef}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 28,
        maxWidth: VIEW_WIDTH[view],
        margin: '0 auto',
        padding: '2rem 0',
        width: '100%',
      }}
    >
      {view === 'ideas' && catalog && (
        <IdeasBrowser
          catalog={catalog}
          business={business}
          initialCategory={ideasCategory}
          onChangeBusiness={() => setPickerOpen(true)}
          onClearBusiness={() => chooseBusiness(null)}
          onBack={() => { setView('start'); focusPrompt() }}
          onUse={startFromIdea}
          helpsMostAction={helpsMostFor}
        />
      )}

      {view === 'blanks' && catalog && blanksIdea && (
        <FillInBlanks
          blanks={catalog.blanks}
          idea={blanksIdea}
          relatedIdeas={related}
          business={business}
          building={creating}
          onOpenBusiness={() => setPickerOpen(true)}
          onChangeIdea={(i) => { if (idx) setStartedFrom(pickFromIdea(i, business, idx)) }}
          onBack={() => openIdeas(ideasCategory)}
          onBuild={(text) => { setDescription(text); void handleCreateNew({ description: text }) }}
          onQuestions={(sentence, notes) => { if (startedFrom) startGuided(startedFrom, sentence, notes) }}
          onFreeText={(text) => { setDescription(text); setView('start'); focusPrompt() }}
        />
      )}

      {view === 'guided' && startedFrom && (
        <GuidedQuestions
          key={guidedKey}
          description={withSources(description.trim())}
          guide={startedFrom.guide}
          title={startedFrom.from}
          attachments={att.attachments.map(toWire)}
          building={creating}
          initialNotes={guidedNotes}
          appName={title}
          onAppNameChange={setTitle}
          onBack={returnFromGuided}
          onBuild={(d) => { void buildGuided(d) }}
        />
      )}

      {view === 'start' && (
        <>
          <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 12 }}>
            <h1
              style={{
                fontSize: '2.3125rem',
                fontWeight: 700,
                margin: 0,
                background: 'linear-gradient(90deg, #0061D0, #009974)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              What do you want to build?
            </h1>
            <p style={{ margin: '0 auto', maxWidth: 560, fontSize: '1.1125rem', lineHeight: 1.5, color: 'var(--color-text-secondary)' }}>
              Describe it the way you'd tell a teammate. One sentence is enough — we'll ask about the rest.
            </p>
          </div>

          {/* The one box. Its toolbar holds the business chip and the shared
              "+" add menu (file / data source), so the rest of the page can
              stay quiet. */}
          <div style={composer}>
            <label htmlFor="hub-desc" style={srOnly}>Describe your app</label>
            <textarea
              id="hub-desc"
              ref={descRef}
              autoFocus
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={business
                ? `What should it do for your ${business.name.toLowerCase()} business? Or tap an idea below to start from it.`
                : 'e.g. Track purchase requests — employees raise them, managers approve, and the purchase team turns them into orders.'}
              disabled={creating}
              rows={4}
              style={promptStyle}
              {...att.inputHandlers}
            />

            {startedFrom && (
              <div style={{ margin: '0 18px 6px', display: 'flex', flexDirection: 'column', gap: 6, fontSize: '0.8825rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', color: 'var(--color-text-secondary)' }}>
                  <Compass size={13} style={{ color: 'var(--color-success)' }} />
                  <span>Started from <b style={{ fontWeight: 500, color: 'var(--color-text)' }}>{startedFrom.from}</b></span>
                  <button
                    type="button"
                    title="Forget this starting point"
                    aria-label="Forget this starting point"
                    onClick={() => setStartedFrom(null)}
                    disabled={creating}
                    style={{ display: 'inline-flex', border: 'none', background: 'transparent', cursor: 'pointer', padding: 2, color: 'var(--color-text-secondary)' }}
                  >
                    <X size={12} />
                  </button>
                  <span>— edit it to fit, or</span>
                  {startedFrom.idea && catalog?.blanks && (
                    <>
                      <button type="button" style={inlineLink} disabled={creating} onClick={() => setView('blanks')}>fill in the blanks</button>
                      <span aria-hidden="true">·</span>
                    </>
                  )}
                  <button type="button" style={inlineLink} disabled={creating}
                    onClick={() => startGuided(startedFrom, description)}>answer a few questions</button>
                </div>
                {startedFrom.solution?.helps_most && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--color-text-secondary)', flexWrap: 'wrap' }}>
                    <span><b style={{ fontWeight: 500, color: 'var(--color-text)' }}>Helps most:</b> {startedFrom.solution.helps_most}</span>
                    {startedHelps && (
                      <button type="button" className="btn btn-ghost btn-sm" onClick={startedHelps} disabled={creating || preparingSource}>
                        <Plus size={13} /> Add it
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {nudgeDims.length > 0 && (
              <div style={nudgeBox}>
                <span style={{ fontSize: '0.9225rem', fontWeight: 500 }}>
                  Good start. Mentioning any of these helps the first version fit:
                </span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {nudgeDims.map((d) => {
                    const done = answeredAfter(description, d.asked_as)
                    return (
                      <button key={d.id} type="button" disabled={creating} onClick={() => addNudge(d.asked_as)}
                        aria-pressed={done} title={done ? 'Answered' : 'Add this question to your description'}
                        style={nudgeChip(done)}>
                        {done ? <Check size={13} /> : <Plus size={13} />}{d.asked_as}
                      </button>
                    )
                  })}
                </div>
              </div>
            )}

            <div style={{ padding: '0 14px' }}>
              <AttachmentChips attachments={att.attachments} onRemove={att.remove} disabled={creating} />
              {sources.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem', marginBottom: '0.375rem' }}>
                  {sources.map((s, i) => (
                    <span key={`${s.kind}-${i}`} style={sourceChip}>
                      <span className="material-icons" style={{ fontSize: 15 }}>
                        {SOURCE_TYPES.find((t) => t.kind === s.kind)?.icon || 'link'}
                      </span>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.label}</span>
                      <button
                        type="button"
                        onClick={() => setSources((prev) => prev.filter((_, j) => j !== i))}
                        disabled={creating}
                        title="Remove source"
                        aria-label={`Remove ${s.label}`}
                        style={{ display: 'inline-flex', border: 'none', background: 'transparent', cursor: 'pointer', padding: 0, color: 'var(--color-text-secondary)' }}
                      >
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            <input
              ref={att.fileInputRef}
              type="file"
              multiple
              accept={ATTACHMENT_ACCEPT}
              onChange={att.onFileInputChange}
              style={{ display: 'none' }}
            />

            <div style={toolbar}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', minWidth: 0 }}>
                {catalog && <BusinessChip business={business} onClick={() => setPickerOpen(true)} disabled={creating} />}
                {/* One "+" control — the SAME add menu as the Ask-AI dock (attach
                    a file OR add a data source). */}
                <AddSourceButton
                  onAttachFile={att.openFilePicker}
                  onSelectSource={(k) => { void openSourceModal(k) }}
                  disabled={creating || preparingSource}
                  loading={preparingSource}
                  placement="bottom"
                  align="left"
                  background="var(--color-bg)"
                  title="Attach a file, or add a data source to build on — a database connection, git/zip source or IOFlow connector"
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: '0.9375rem', fontWeight: 500, cursor: 'pointer', marginRight: 'auto' }}>
                  <input
                    type="checkbox"
                    checked={reviewFirst}
                    onChange={(e) => setReviewFirst(e.target.checked)}
                    disabled={creating}
                    style={{ width: 17, height: 17, accentColor: 'var(--color-accent)' }}
                  />
                  Show me the plan before anything is built
                </label>
                {trimmed.length > 0 && trimmed.length < 10 && (
                  <span style={{ fontSize: '0.8425rem', color: 'var(--color-text-secondary)' }}>
                    A sentence or two is enough — what should the app do?
                  </span>
                )}
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => { void handleCreateNew() }}
                  disabled={creating || trimmed.length < 10}
                  title={nudgeDims.length ? 'You can build now — anything missing, the builder asks before it starts' : undefined}
                >
                  {creating ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Sparkles size={16} />}
                  {creating ? 'Setting up…' : 'Build it'}
                </button>
              </div>
            </div>
          </div>

          {popular.length > 0 && (
            <section aria-labelledby="hub-ideas" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <h2 id="hub-ideas" style={{ margin: 0, fontSize: '1.0125rem', fontWeight: 600 }}>
                  {business?.id && popular.some((i) => i.industries !== '*')
                    ? `Popular in ${business.name.toLowerCase()}`
                    : 'Popular ideas'}
                </h2>
                <button type="button" style={{ ...inlineLink, textDecoration: 'none', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 2 }}
                  onClick={() => openIdeas(null)}>
                  Browse all ideas <ChevronRight size={14} />
                </button>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {popular.map((i) => (
                  <button key={i.key} type="button" disabled={creating} onClick={() => startFromIdea(i, 'describe')} style={ideaChip}>
                    <Plus size={13} style={{ color: 'var(--color-accent)', flex: 'none' }} />{i.title}
                  </button>
                ))}
              </div>
              {!business && (
                <p style={{ margin: 0, fontSize: '0.8825rem', color: 'var(--color-text-secondary)' }}>
                  These suit most businesses.{' '}
                  <button type="button" style={inlineLink} onClick={() => setPickerOpen(true)}>Tell us your business</button>
                  {' '}to see ideas made for it.
                </p>
              )}
            </section>
          )}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            <button type="button" style={door} disabled={creating} onClick={notSure}>
              <span style={doorIcon}><HelpCircle size={19} /></span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontWeight: 600 }}>Not sure at all?</span>
                <span style={doorSub}>Answer a few quick questions — each has a suggested answer.</span>
              </span>
            </button>
            {doors.map((g) => (
              <button key={g.id} type="button" style={door} disabled={creating} onClick={() => openIdeas(`g:${g.id}`)}>
                <span style={doorIcon}><Database size={19} /></span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontWeight: 600 }}>{g.name}</span>
                  <span style={doorSub}>{g.summary}.</span>
                </span>
              </button>
            ))}
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 24, fontSize: '0.9425rem' }}>
            <button type="button" style={quietLink} onClick={() => setProjectModalOpen(true)}>Continue a project</button>
            <button type="button" style={quietLink} onClick={() => navigate('/samples')}>Browse sample apps</button>
          </div>

          <AdvancedOptions />
        </>
      )}

      {/* "Add a source" — registers through the same APIs (and the same
          connection editor) the Ask-AI composer uses. Persisting sources are
          staged into "Draft Application"; Build moves them into the named
          project via adopt-draft-sources. */}
      <AddSourceModal
        open={sourceModalKind !== null}
        kind={sourceModalKind}
        project="Draft Application"
        onClose={() => setSourceModalKind(null)}
        onAttach={(s) => setSources((prev) => [...prev, s])}
        onPickSpecFile={att.openFilePicker}
      />

      {catalog && (
        <IndustryPicker
          open={pickerOpen}
          catalog={catalog}
          currentId={business?.id || null}
          title={pickerThen === 'guided' ? "First, what's your business?" : undefined}
          onPick={chooseBusiness}
          onClose={() => { setPickerOpen(false); setPickerThen(null) }}
        />
      )}
    </div>
  )
}

// ── styles ────────────────────────────────────────────────────────────
const srOnly: CSSProperties = {
  position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap',
}
const composer: CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 8, background: 'var(--color-bg)',
  border: '1px solid var(--color-border-strong)', borderRadius: 14,
  boxShadow: '0 8px 24px var(--color-shadow, rgba(0,0,0,.06))',
}
const promptStyle: CSSProperties = {
  display: 'block', width: '100%', boxSizing: 'border-box', minHeight: 128, border: 0, resize: 'vertical',
  padding: '18px 18px 6px', font: 'inherit', fontSize: '1.0625rem', lineHeight: 1.55, color: 'var(--color-text)',
  // The composer card is the field — no global field chrome inside it.
  background: 'transparent', outline: 'none', borderRadius: 0, boxShadow: 'none',
}
const toolbar: CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap',
  padding: '4px 12px 12px',
}
const inlineLink: CSSProperties = {
  background: 'none', border: 0, padding: 0, font: 'inherit', color: 'var(--color-accent)',
  cursor: 'pointer', textDecoration: 'underline',
}
const quietLink: CSSProperties = {
  background: 'none', border: 0, padding: '6px 0', font: 'inherit', color: 'var(--color-text-secondary)',
  cursor: 'pointer', textDecoration: 'underline',
}
const nudgeBox: CSSProperties = {
  margin: '0 12px', padding: 12, borderRadius: 10, background: 'var(--color-bg-secondary)',
  border: '1px solid var(--color-border)', display: 'flex', flexDirection: 'column', gap: 8,
}
const nudgeChip = (done: boolean): CSSProperties => ({
  font: 'inherit', fontSize: '0.9025rem', display: 'inline-flex', alignItems: 'center', gap: 5, minHeight: 34,
  padding: '4px 12px', borderRadius: 999, cursor: 'pointer', textAlign: 'left',
  border: `1px solid ${done ? 'var(--color-success)' : 'var(--color-border-strong)'}`,
  background: done ? 'var(--color-success-weak)' : 'var(--color-bg)',
  color: done ? 'var(--color-success)' : 'var(--color-text)',
})
const sourceChip: CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: '0.375rem', padding: '0.1875rem 0.5rem',
  fontSize: '0.875rem', background: 'var(--color-bg)', border: '1px solid var(--color-border)',
  borderRadius: '999px', color: 'var(--color-text)', maxWidth: '100%',
}
const ideaChip: CSSProperties = {
  font: 'inherit', fontSize: '0.9425rem', display: 'inline-flex', alignItems: 'center', gap: 7, minHeight: 38,
  padding: '6px 14px', borderRadius: 999, cursor: 'pointer', textAlign: 'left',
  border: '1px solid var(--color-border-strong)', background: 'var(--color-bg)', color: 'var(--color-text)',
}
const door: CSSProperties = {
  font: 'inherit', flex: '1 1 300px', display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px',
  borderRadius: 12, border: '1px solid var(--color-border)', background: 'var(--color-bg-secondary)',
  color: 'var(--color-text)', textAlign: 'left', cursor: 'pointer',
}
const doorIcon: CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 38, height: 38, flex: 'none',
  borderRadius: 999, background: 'var(--color-bg)', color: 'var(--color-accent)',
}
const doorSub: CSSProperties = {
  display: 'block', marginTop: 2, fontSize: '0.9225rem', color: 'var(--color-text-secondary)',
}

/* ── Advanced options disclosure ─────────────────────────────────── */
//
// The technical step-by-step flows live here: the FRS pipeline (where the
// user authors a specification first, then generates) and the Git import.
// Both are perfectly valid; they just need more clicks than the chat-first
// composer above, so they're tucked under a single disclosure rather than
// taking up real estate on the hub.

function AdvancedOptions() {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  // "Let me build myself" reveals the SAME Start-blank form used on the
  // step-by-step FRS page (/generateapp), inline under the cards.
  const [blankOpen, setBlankOpen] = useState(false)
  const Chevron = open ? ChevronDown : ChevronRight

  return (
    <div style={{ marginTop: 8 }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        style={{
          background: 'transparent',
          border: 'none',
          color: 'var(--color-text-secondary)',
          fontSize: 14,
          fontWeight: 500,
          padding: '8px 0',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          cursor: 'pointer',
        }}
      >
        <Chevron size={14} />
        Advanced options
      </button>

      {open && (
        <div
          style={{
            marginTop: 12,
            padding: 16,
            border: '1px solid var(--color-border)',
            borderRadius: 12,
            background: 'var(--color-bg-secondary)',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 12,
          }}
        >
          <StartCard
            icon={FileText}
            title="Step-by-step with FRS"
            blurb="Author a Functional Requirement Specification first, review it, then generate the app. More control, more clicks."
            cta="Open FRS builder"
            onClick={() => navigate('/generateapp')}
          />
          <StartCard
            icon={GitBranch}
            title="Import from Git"
            blurb="Clone an existing Twasta project from a Git repository."
            cta="Pull from Git"
            onClick={() => navigate('/create-from-git')}
          />
          <StartCard
            icon={Hammer}
            title="Let me build myself"
            blurb="No AI at all. Start from an empty project and design every Transaction, Smart Page, and Dashboard by hand."
            cta={blankOpen ? 'Hide form' : 'Start blank'}
            onClick={() => setBlankOpen((v) => !v)}
          />
        </div>
      )}

      {open && blankOpen && (
        <div style={{ marginTop: 12 }}>
          <StartBlankForm />
        </div>
      )}
    </div>
  )
}
